import React from "react";
import { toast } from "sonner";
import { Share, WhatsappLogo, FacebookLogo, Link as LinkIcon } from "@phosphor-icons/react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import {
  shareViaWhatsApp, shareViaFacebook, copyLinkToClipboard,
  shareViaNative, canShareNatively,
} from "../lib/share";

// ShareMenu — canonical share surface across the app.
// Uses native share sheet on mobile when available; falls back to channel buttons.
// Feature-flag aware via config.features (WhatsApp/Facebook/Copy always in Buyer Experience).
export const ShareMenu = ({ property, variant = "icon", className = "" }) => {
  const [open, setOpen] = React.useState(false);

  const tryNative = async () => {
    const ok = await shareViaNative(property);
    if (ok) setOpen(false);
    else setOpen(true);
  };

  const handleCopy = async () => {
    const ok = await copyLinkToClipboard(property);
    if (ok) toast.success("Enlace copiado");
    else toast.error("No pudimos copiar el enlace");
    setOpen(false);
  };

  const trigger = variant === "icon" ? (
    <Button
      variant="outline"
      size="icon"
      onClick={canShareNatively() ? (e) => { e.preventDefault(); tryNative(); } : undefined}
      className={`border-[#C8A24A]/30 hover:bg-[#C8A24A]/10 ${className}`}
      data-testid={`share-btn-${property.id}`}
      aria-label="Compartir propiedad"
    >
      <Share size={18} className="text-[#C8A24A]" />
    </Button>
  ) : (
    <Button
      variant="outline"
      onClick={canShareNatively() ? (e) => { e.preventDefault(); tryNative(); } : undefined}
      className={`border-[#C8A24A]/30 hover:bg-[#C8A24A]/10 text-[#C8A24A] ${className}`}
      data-testid={`share-btn-${property.id}`}
    >
      <Share size={16} className="mr-2" /> Compartir
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="bg-[#1A1A1A] border border-[#C8A24A]/20 text-[#C8A24A] sm:max-w-md" data-testid="share-menu">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Compartir propiedad</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-3 gap-3 py-2">
          <ShareOption
            testid="share-whatsapp"
            icon={<WhatsappLogo size={28} weight="fill" />}
            label="WhatsApp"
            onClick={() => { shareViaWhatsApp(property); setOpen(false); }}
          />
          <ShareOption
            testid="share-facebook"
            icon={<FacebookLogo size={28} weight="fill" />}
            label="Facebook"
            onClick={() => { shareViaFacebook(property); setOpen(false); }}
          />
          <ShareOption
            testid="share-copy"
            icon={<LinkIcon size={26} />}
            label="Copiar link"
            onClick={handleCopy}
          />
        </div>
        <p className="text-xs text-[#C8A24A]/60 leading-relaxed border-t border-[#C8A24A]/15 pt-3 mt-1" data-testid="fb-photo-note-menu">
          Facebook mostrará automáticamente la foto principal de la propiedad. Para publicar varias fotos, agrégalas manualmente desde Facebook.
        </p>
      </DialogContent>
    </Dialog>
  );
};

const ShareOption = ({ icon, label, onClick, testid }) => (
  <button
    onClick={onClick}
    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-[#0A0A0A] border border-[#C8A24A]/20 hover:border-[#C8A24A] hover:bg-[#C8A24A]/5 transition-colors"
    data-testid={testid}
  >
    <span className="text-[#C8A24A]">{icon}</span>
    <span className="text-xs font-medium text-[#C8A24A]/80">{label}</span>
  </button>
);
